import React, { useMemo, useState } from 'react';
import Header from '@/components/Header';
import PromiseCard from '@/components/PromiseCard';
import PromiseList from '@/components/PromiseList';
import ViewToggle, { ViewType } from '@/components/ViewToggle';
import FilterSection, { FilterOptions } from '@/components/FilterSection';
import { promises, PromiseSector } from '@/data/promises';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from '@/components/ui/button';
import { Menu } from 'lucide-react';
import Footer from '@/components/Footer';
import PoliticianStats from '@/components/PoliticianStats';

const Index = () => {
  const [viewType, setViewType] = useState<ViewType>('card');
  const [filters, setFilters] = useState<FilterOptions>({
    politician: null,
    politicalParty: null,
    status: null,
    sector: null,
    year: null,
  });
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const { politicians, parties, sectors, years } = useMemo(() => ({
    politicians: Array.from(new Set(promises.map((p) => p.politician))).sort(),
    parties: Array.from(new Set(promises.map((p) => p.politicalParty))).sort(),
    sectors: Array.from(new Set(promises.map((p) => p.sector))),
    years: Array.from(
      new Set(promises.map((p) => new Date(p.dateMade).getFullYear().toString()))
    ).sort((a, b) => parseInt(b) - parseInt(a))
  }), []);

  const filteredPromises = useMemo(() => {
    return promises.filter((promise) => {
      if (filters.politician && promise.politician !== filters.politician) return false;
      if (filters.politicalParty && promise.politicalParty !== filters.politicalParty) return false;
      if (filters.status && promise.status !== filters.status) return false;
      if (filters.sector && promise.sector !== filters.sector) return false;
      if (filters.year) {
        const promiseYear = new Date(promise.dateMade).getFullYear().toString();
        if (promiseYear !== filters.year) return false;
      }
      return true;
    });
  }, [filters]);

  const handleFilterChange = (key: keyof FilterOptions, value: string | null) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    if (window.innerWidth < 1024) {
        setIsSheetOpen(false);
    }
  };

  const handleClearFilters = () => {
    setFilters({
      politician: null,
      politicalParty: null,
      status: null,
      sector: null,
      year: null,
    });
    if (window.innerWidth < 1024) {
      setIsSheetOpen(false);
    }
  };

  const handleMultipleFilters = (newFilters: Partial<FilterOptions>) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters
    }));
    if (window.innerWidth < 1024) {
      setIsSheetOpen(false);
    }
  };

  const filterProps = {
    filters,
    onFilterChange: handleFilterChange,
    onClearFilters: handleClearFilters,
    politicians,
    parties,
    sectors: sectors as PromiseSector[],
    years,
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <div className="container px-4 py-8 flex-grow">
        <PoliticianStats onFilterChange={handleMultipleFilters} />
        
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <div className="flex items-center gap-4">
            <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="lg:hidden">
                  <Menu className="h-4 w-4" />
                  <span className="sr-only">Open Filters</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-full max-w-xs p-0">
                <SheetHeader className="p-4 border-b">
                    <SheetTitle>Filters</SheetTitle>
                </SheetHeader>
                <div className="p-4 overflow-y-auto">
                    <FilterSection {...filterProps} />
                </div>
              </SheetContent>
            </Sheet>

            <div>
                <h2 className="text-xl font-semibold text-gray-800">
                {filteredPromises.length} {filteredPromises.length === 1 ? 'Promise' : 'Promises'} Found
                </h2>
                <p className="text-sm text-gray-500">
                {viewType === 'card' ? 'Card view' : 'List view'} •
                {Object.values(filters).some(Boolean) ? ' Filtered results' : ' Showing all'}
                </p>
            </div>
          </div>
          <ViewToggle currentView={viewType} onViewChange={setViewType} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            <aside className="hidden lg:block lg:col-span-1">
                <div className="sticky top-4 space-y-6">
                    <h3 className="text-lg font-semibold">Filters</h3>
                    <FilterSection {...filterProps} />
                </div>
            </aside>

            <main className="lg:col-span-3 space-y-6">
                {viewType === 'card' ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filteredPromises.map((promise) => (
                        <PromiseCard key={promise.id} promise={promise} />
                    ))}
                    </div>
                ) : (
                    <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                    <PromiseList promises={filteredPromises} />
                    </div>
                )}

                {filteredPromises.length === 0 && (
                    <div className="text-center py-16 bg-white rounded-lg shadow-sm border">
                    <h3 className="text-lg font-medium text-gray-800">No promises match your filters</h3>
                    <p className="text-gray-500 mt-2">Try adjusting your filter criteria</p>
                    </div>
                )}
            </main>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Index;
